import { scroll, getScrollableParent } from '@seek/ca-myprofile-utils';
import { type RefObject, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DRAWER_CARDS } from 'src/components/Router';
import { isSmallDevice } from 'src/modules/device';
import type { Card, DrawerCard } from 'src/types';

interface ScrollOptions {
  target?: string;
  desktopOffset?: number;
  mobileOffset?: number;
  minDuration?: number;
  speed?: number;
  delay?: number;
}

export interface CardField<T extends HTMLElement> {
  card: Card;
  fieldName: string;
  scrollOptions?: ScrollOptions;
  action?: (ref: T) => void;
  domReady?: boolean;
  forceDesktopScrolling?: boolean;
}

export const DEFAULT_MOBILE_OFFSET = 70;
export const DEFAULT_DESKTOP_OFFSET = 210;

const defaultFieldAction = <T extends HTMLElement>(ref: T) => ref.focus();

export const useFocus = <T extends HTMLElement>({
  card,
  fieldName,
  scrollOptions = {},
  action = defaultFieldAction,
  domReady = true,
  forceDesktopScrolling = false,
}: CardField<T>): RefObject<T> => {
  const ref = useRef<T>(null);
  const [params] = useSearchParams();
  const shouldFocus =
    params.get('focusOn') === fieldName || params.get('field') === fieldName;
  const isCypressTest =
    typeof window !== 'undefined' && typeof window.Cypress !== 'undefined';

  const focusField = useCallback(() => {
    const isMobile = isSmallDevice();

    const {
      target,
      desktopOffset = DEFAULT_DESKTOP_OFFSET,
      mobileOffset = DEFAULT_MOBILE_OFFSET,
      minDuration = isCypressTest ? 1 : 400,
      speed = 2,
      delay = isCypressTest ? 1 : 0,
    }: ScrollOptions = scrollOptions;
    const scrollTarget = target || ref.current;
    const offset = isMobile ? mobileOffset : desktopOffset;

    if (scrollTarget) {
      if (
        isMobile &&
        !forceDesktopScrolling &&
        !DRAWER_CARDS.includes(card as DrawerCard)
      ) {
        if (typeof scrollTarget !== 'string') {
          const scrollPanel = getScrollableParent(scrollTarget);
          if (scrollPanel) {
            scrollPanel.scrollTop =
              scrollTarget.getBoundingClientRect().top - offset;
            action(scrollTarget);
          }
        }
      } else {
        scroll(scrollTarget, {
          offset,
          minDuration,
          speed,
          delay,
          duration: isCypressTest ? 1 : undefined,
        }).then(() => {
          if (ref.current) {
            action(ref.current);
          }
        });
      }
    }
  }, [action, card, forceDesktopScrolling, isCypressTest, scrollOptions]);

  useEffect(() => {
    if (domReady && shouldFocus) {
      focusField();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domReady, shouldFocus]);

  return ref;
};
